import React, { createContext, useReducer, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import HelpContentProvider from '../../services/HelpContentProvider';
import {
  DialogId,
  DialogState,
  DialogConfig,
  DialogGlobalConfig,
  DialogProviderContext,
  DialogProviderState,
  DialogAction,
  DialogActionType,
  DialogStack,
  DialogHelpSystem,
  DEFAULT_DIALOG_GLOBAL_CONFIG,
  DEFAULT_DIALOG_CONFIG,
} from './DialogProvider.types';
import {
  createDialogStack,
  getNextZIndex,
  canOpenDialog,
  updateAllZIndices,
  closeChildDialogs,
  findFocusTargetAfterClose,
  restoreFocus,
  validateDialogConfig,
  generateDialogId,
  getCurrentFocusElement,
  handleEscapeKeyForStack,
  debugDialogStack,
} from './DialogProvider.utils';

/**
 * Initial state for the dialog provider
 */
const initialState: DialogProviderState = {
  dialogs: new Map(),
  globalConfig: DEFAULT_DIALOG_GLOBAL_CONFIG,
};

/**
 * Dialog provider reducer
 */
const dialogReducer = (
  state: DialogProviderState,
  action: DialogAction
): DialogProviderState => {
  switch (action.type) {
    case DialogActionType.REGISTER_DIALOG: {
      const { id, config } = action.payload;
      const newDialogs = new Map(state.dialogs);

      // Don't re-register if already exists
      if (newDialogs.has(id)) {
        console.warn(`Dialog ${id} is already registered`);
        return state;
      }

      // Validate configuration (only if config is provided)
      if (config && !validateDialogConfig(config)) {
        console.error(`Invalid configuration for dialog ${id}`);
        return state;
      }

      const dialogState: DialogState = {
        id,
        isOpen: false,
        config: { ...DEFAULT_DIALOG_CONFIG, ...config },
        zIndex: 0, // Will be assigned when opened
        childIds: [],
        openedAt: 0, // Will be set when opened
      };

      newDialogs.set(id, dialogState);

      return {
        ...state,
        dialogs: newDialogs,
      };
    }

    case DialogActionType.UNREGISTER_DIALOG: {
      const { id } = action.payload;
      const newDialogs = new Map(state.dialogs);
      const dialog = newDialogs.get(id);

      if (!dialog) {
        console.warn(`Dialog ${id} is not registered`);
        return state;
      }

      // Close dialog if it's open
      if (dialog.isOpen) {
        // Close all children first
        closeChildDialogs(id, newDialogs);

        // Restore focus if needed
        restoreFocus(dialog, state.globalConfig);

        // Call close callback
        dialog.config.onClose?.('unregister');
      }

      // Remove from parent's children list
      if (dialog.parentId) {
        const parent = newDialogs.get(dialog.parentId);
        if (parent) {
          const updatedParent: DialogState = {
            ...parent,
            childIds: parent.childIds.filter(childId => childId !== id),
          };
          newDialogs.set(dialog.parentId, updatedParent);
        }
      }

      newDialogs.delete(id);

      // Update z-indices after removal
      const updatedDialogs = updateAllZIndices(newDialogs, state.globalConfig);

      return {
        ...state,
        dialogs: updatedDialogs,
      };
    }

    case DialogActionType.OPEN_DIALOG: {
      const { id, parentId } = action.payload;
      console.log(`OPEN_DIALOG reducer:`, { id, parentId, dialogsSize: state.dialogs.size });
      const newDialogs = new Map(state.dialogs);
      const dialog = newDialogs.get(id);
      console.log(`Dialog found:`, dialog);

      if (!dialog) {
        console.error(`Cannot open unregistered dialog: ${id}. Available dialogs:`, Array.from(state.dialogs.keys()));
        return state;
      }

      if (dialog.isOpen) {
        console.warn(`Dialog ${id} is already open`);
        return state;
      }

      // Check stack limits
      if (!canOpenDialog(newDialogs, state.globalConfig)) {
        console.warn('Maximum dialog stack depth reached');
        return state;
      }

      // Store current focus element
      const previousFocusElement = getCurrentFocusElement();

      // Assign z-index
      const zIndex = dialog.config.zIndex || getNextZIndex(newDialogs, state.globalConfig);

      // Update dialog state
      const updatedDialog: DialogState = {
        ...dialog,
        isOpen: true,
        zIndex,
        parentId,
        openedAt: Date.now(),
        previousFocusElement: previousFocusElement || undefined,
      };

      newDialogs.set(id, updatedDialog);

      // Add to parent's children list if applicable
      if (parentId) {
        const parent = newDialogs.get(parentId);
        if (parent) {
          const updatedParent: DialogState = {
            ...parent,
            childIds: [...parent.childIds, id],
          };
          newDialogs.set(parentId, updatedParent);
        }
      }

      // Update all z-indices to maintain order
      const finalDialogs = updateAllZIndices(newDialogs, state.globalConfig);

      // Call open callback
      updatedDialog.config.onOpen?.();

      return {
        ...state,
        dialogs: finalDialogs,
      };
    }

    case DialogActionType.CLOSE_DIALOG: {
      const { id, reason } = action.payload;
      const newDialogs = new Map(state.dialogs);
      const dialog = newDialogs.get(id);

      if (!dialog || !dialog.isOpen) {
        console.warn(`Dialog ${id} is not open`);
        return state;
      }

      // Close all children first
      const closedChildIds = closeChildDialogs(id, newDialogs, reason);
      closedChildIds.forEach(childId => {
        const child = newDialogs.get(childId);
        if (child) {
          newDialogs.set(childId, { ...child, isOpen: false });
        }
      });

      // Close the dialog
      const updatedDialog: DialogState = {
        ...dialog,
        isOpen: false,
        zIndex: 0,
        openedAt: 0,
      };

      newDialogs.set(id, updatedDialog);

      // Remove from parent's children list
      if (dialog.parentId) {
        const parent = newDialogs.get(dialog.parentId);
        if (parent) {
          const updatedParent: DialogState = {
            ...parent,
            childIds: parent.childIds.filter(childId => childId !== id),
          };
          newDialogs.set(dialog.parentId, updatedParent);
        }
      }

      // Update z-indices after closing
      const finalDialogs = updateAllZIndices(newDialogs, state.globalConfig);

      // Restore focus
      restoreFocus(dialog, state.globalConfig);

      // Call close callback
      dialog.config.onClose?.(reason);

      return {
        ...state,
        dialogs: finalDialogs,
      };
    }

    case DialogActionType.CLOSE_ALL_DIALOGS: {
      const { reason } = action.payload || { reason: 'close-all' };
      const newDialogs = new Map(state.dialogs);

      // Close all open dialogs
      Array.from(newDialogs.values())
        .filter(dialog => dialog.isOpen)
        .forEach(dialog => {
          const updatedDialog: DialogState = {
            ...dialog,
            isOpen: false,
            zIndex: 0,
            openedAt: 0,
            childIds: [], // Clear children
          };
          newDialogs.set(dialog.id, updatedDialog);

          // Restore focus and call callback
          restoreFocus(dialog, state.globalConfig);
          dialog.config.onClose?.(reason);
        });

      return {
        ...state,
        dialogs: newDialogs,
      };
    }

    case DialogActionType.UPDATE_DIALOG_CONFIG: {
      const { id, config } = action.payload;
      const newDialogs = new Map(state.dialogs);
      const dialog = newDialogs.get(id);

      if (!dialog) {
        console.warn(`Dialog ${id} is not registered`);
        return state;
      }

      // Validate new configuration
      if (config && !validateDialogConfig(config)) {
        console.error(`Invalid configuration update for dialog ${id}`);
        return state;
      }

      const updatedDialog: DialogState = {
        ...dialog,
        config: { ...dialog.config, ...config },
      };

      newDialogs.set(id, updatedDialog);

      return {
        ...state,
        dialogs: newDialogs,
      };
    }

    case DialogActionType.UPDATE_GLOBAL_CONFIG: {
      const { config } = action.payload;
      const newGlobalConfig = { ...state.globalConfig, ...config };

      // Update z-indices if z-index related config changed
      let newDialogs = state.dialogs;
      if (
        config.baseZIndex !== undefined ||
        config.zIndexIncrement !== undefined
      ) {
        newDialogs = updateAllZIndices(state.dialogs, newGlobalConfig);
      }

      return {
        ...state,
        dialogs: newDialogs,
        globalConfig: newGlobalConfig,
      };
    }

    default:
      return state;
  }
};

/**
 * DialogProvider component
 */
interface DialogProviderProps {
  children: React.ReactNode;
  globalConfig?: Partial<DialogGlobalConfig>;
}

export const DialogProvider: React.FC<DialogProviderProps> = React.memo(({
  children,
  globalConfig: userGlobalConfig,
}) => {
  // Simplified initialization without theme dependencies to avoid loops
  const [state, dispatch] = useReducer(dialogReducer, {
    ...initialState,
    globalConfig: {
      ...DEFAULT_DIALOG_GLOBAL_CONFIG,
      ...userGlobalConfig,
    },
  });

  // Create dialog stack
  const stack: DialogStack = useMemo(() => {
    return createDialogStack(state.dialogs, state.globalConfig);
  }, [state.dialogs, state.globalConfig]);

  // Help system integration
  const helpSystem: DialogHelpSystem = useMemo(() => {
    const helpProvider = HelpContentProvider.getInstance();

    return {
      getContent: async (helpId: string) => {
        return await helpProvider.getContentAsync(helpId);
      },
      registerContent: (helpId: string, content: string) => {
        helpProvider.registerContent(helpId, content);
      },
      hasContent: (helpId: string) => {
        return helpProvider.hasContent(helpId);
      },
    };
  }, []);

  // Action creators
  const registerDialog = useCallback((id: DialogId, config?: Partial<DialogConfig>) => {
    dispatch({
      type: DialogActionType.REGISTER_DIALOG,
      payload: { id, config },
    });
  }, []);

  const unregisterDialog = useCallback((id: DialogId) => {
    dispatch({
      type: DialogActionType.UNREGISTER_DIALOG,
      payload: { id },
    });
  }, []);

  const openDialog = useCallback((id: DialogId, parentId?: DialogId) => {
    console.log(`DialogProvider.openDialog called:`, { id, parentId });
    dispatch({
      type: DialogActionType.OPEN_DIALOG,
      payload: { id, parentId },
    });
  }, []); // Remove state.dialogs dependency to prevent re-creation

  const closeDialog = useCallback((id: DialogId, reason?: string) => {
    dispatch({
      type: DialogActionType.CLOSE_DIALOG,
      payload: { id, reason },
    });
  }, []);

  const closeAllDialogs = useCallback((reason?: string) => {
    dispatch({
      type: DialogActionType.CLOSE_ALL_DIALOGS,
      payload: { reason },
    });
  }, []);

  const closeTopDialog = useCallback((reason?: string) => {
    if (stack.activeId) {
      closeDialog(stack.activeId, reason);
    }
  }, [stack.activeId, closeDialog]);

  const getZIndex = useCallback((id: DialogId): number => {
    const dialog = state.dialogs.get(id);
    return dialog?.zIndex || 0;
  }, [state.dialogs]);

  const getTopDialogId = useCallback((): DialogId | null => {
    return stack.activeId;
  }, [stack.activeId]);

  const isDialogOpen = useCallback((id: DialogId): boolean => {
    const dialog = state.dialogs.get(id);
    return dialog?.isOpen || false;
  }, [state.dialogs]);

  const updateDialogConfig = useCallback((id: DialogId, config: Partial<DialogConfig>) => {
    dispatch({
      type: DialogActionType.UPDATE_DIALOG_CONFIG,
      payload: { id, config },
    });
  }, []);

  const updateGlobalConfig = useCallback((config: Partial<DialogGlobalConfig>) => {
    dispatch({
      type: DialogActionType.UPDATE_GLOBAL_CONFIG,
      payload: { config },
    });
  }, []);

  const handleEscapeKey = useCallback(() => {
    const dialogsToClose = handleEscapeKeyForStack(stack, state.dialogs, state.globalConfig);
    dialogsToClose.forEach(id => closeDialog(id, 'escape'));
  }, [stack, state.dialogs, state.globalConfig, closeDialog]);

  const handleBackdropClick = useCallback((id: DialogId) => {
    const dialog = state.dialogs.get(id);
    if (dialog && !dialog.config.disableBackdropClick) {
      closeDialog(id, 'backdrop');
    }
  }, [state.dialogs, closeDialog]);

  // Global ESC key handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && stack.count > 0) {
        handleEscapeKey();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleEscapeKey, stack.count]);

  // Debug logging in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugDialogStack(state.dialogs, stack);
    }
  }, [state.dialogs, stack]);

  // Context value
  const contextValue: DialogProviderContext = useMemo(() => ({
    // State
    dialogs: state.dialogs,
    stack,
    globalConfig: state.globalConfig,
    helpSystem,

    // Actions
    registerDialog,
    unregisterDialog,
    openDialog,
    closeDialog,
    closeAllDialogs,
    closeTopDialog,
    getZIndex,
    getTopDialogId,
    isDialogOpen,
    updateDialogConfig,
    updateGlobalConfig,
    handleEscapeKey,
    handleBackdropClick,
  }), [
    state.dialogs,
    stack,
    state.globalConfig,
    helpSystem,
    registerDialog,
    unregisterDialog,
    openDialog,
    closeDialog,
    closeAllDialogs,
    closeTopDialog,
    getZIndex,
    getTopDialogId,
    isDialogOpen,
    updateDialogConfig,
    updateGlobalConfig,
    handleEscapeKey,
    handleBackdropClick,
  ]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
    </DialogContext.Provider>
  );
});

DialogProvider.displayName = 'DialogProvider';

/**
 * React Context for dialog management
 */
export const DialogContext = createContext<DialogProviderContext | null>(null);

/**
 * Default export
 */
export default DialogProvider;